import { computed, ref } from "vue";
import { isMobileDevice, copyToClipboard } from "@/utils/shareUtils";

type InviteShareMethod = "copy" | "share";

interface InviteShareEvent {
  at: number;
  method: InviteShareMethod;
  url: string;
}

const INVITE_ANON_KEY = "invite_anon_code";
const INVITE_SHARE_HISTORY_KEY = "invite_share_history";

function getOrCreateAnonCode(): string {
  const existing = localStorage.getItem(INVITE_ANON_KEY);
  if (existing) return existing;
  const created = `anon_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(INVITE_ANON_KEY, created);
  return created;
}

function loadHistory(): InviteShareEvent[] {
  try {
    const raw = localStorage.getItem(INVITE_SHARE_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as InviteShareEvent[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(history: InviteShareEvent[]) {
  try {
    localStorage.setItem(INVITE_SHARE_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // ignore
  }
}

export function useInviteFriends(
  /**
   * Accept any ref-like user object (including readonly refs).
   * We only need `ulid` for invite/ref attribution.
   */
  user: { value: { ulid?: string } | null },
) {
  const history = ref<InviteShareEvent[]>(loadHistory());

  const inviteCode = computed(() => {
    // Prefer stable per-user code; fallback to a locally persisted anon code.
    const u = user.value;
    if (u?.ulid) return `u_${u.ulid}`;
    return getOrCreateAnonCode();
  });

  const inviteLink = computed(() => {
    const base = window.location.origin;
    return `${base}/login?ref=${encodeURIComponent(inviteCode.value)}`;
  });

  function record(method: InviteShareMethod) {
    const evt: InviteShareEvent = {
      at: Date.now(),
      method,
      url: inviteLink.value,
    };
    history.value = [evt, ...history.value].slice(0, 50);
    saveHistory(history.value);
  }

  async function copyInviteLink(): Promise<boolean> {
    const success = await copyToClipboard(inviteLink.value);
    if (success) {
      record("copy");
    }
    return success;
  }

  async function shareInviteLink(): Promise<boolean> {
    try {
      // Use Web Share API ONLY on mobile devices
      // Desktop (including Windows): Always copy to clipboard
      if (isMobileDevice() && navigator.share) {
        await navigator.share({
          title: "Join me",
          text: "Join me on this app",
          url: inviteLink.value,
        });
        record("share");
        return true;
      }
      return await copyInviteLink();
    } catch {
      // user cancelled or share failed - fall back to copy
      return await copyInviteLink();
    }
  }

  const totalShares = computed(() => history.value.length);

  return {
    inviteCode,
    inviteLink,
    history,
    totalShares,
    copyInviteLink,
    shareInviteLink,
  };
}


